import { useEffect, useRef, useContext } from 'react';

import { useParams, useNavigate } from 'react-router-dom';

import { AppContext } from '../scripts/context'
import { GetSectionName } from '../scripts/utilities';

export default function Main() {

    const navigate = useNavigate();
    
    const { page } = useParams();
    const { section } = useParams();
    const { item } = useParams();
    
    const itemsSectionRef = useRef();

    const scrollCenterRef = useRef();
    const scrollDistanceRef = useRef();
    const scrollFirstPosRef = useRef();
    const scrollLastPosRef = useRef();

    const {
    //GENERAL
        pageElementsRefs,
    //WIDGET
        setWidgetState,
    //SCROLL
		scrollToBottom,
    //DETAIL
        detailMediaLoadedRef,
        detailMediaLoaderState,
        detailItemActiveRef,
        setDetailWidgetState,
    //STYLE
        mainContainerStyleState,
        mainContainerContentStyleState,
        itemsContainerContentStyleState,
        itemsControlsLeftSvgStyleState,
        setItemsControlsLeftSvgStyleState,
        itemsControlsRightSvgStyleState,
        setItemsControlsRightSvgStyleState
    } = useContext(AppContext);

    const contentMap = {
        home: {
            srcs: ["/media/njbdesign_nils_t.webp"],
            alts: ["njbdesign_nils_t.webp"],
            titles: ["nils badertscher"],
            text:   <div id="text_container">
                        <h1>WELCOME</h1>
                        <h2>Benefit from our expertise to design efficient, reliable and timeless solutions</h2>
                        <p>Over 20 years in digital engineering, <span className="nw">njb design<sup>®</sup></span> obtained an agile set of interoperable skills and procedures. Connecting technologies with a holistic approach enables us to find reliable and timeless solutions for digital production, games development and industrial design. Every project is a piece of our evolution and becomes part of our identity.</p>
                    </div>,
            ids: ["nils"],
            shorts: ["nils"]
        },
        digital: {
            srcs: ["/media/osterwalder_opp2000_t.webp", "/media/meyerburger_fabia_t.webp", "/media/suss_jetx_t.webp", "/media/peterwolters_dw291_t.webp"],
            alts: ["osterwalder_opp2000_t.webp", "meyerburger_fabia_t.webp", "suss_jetx_t.webp", "peterwolters_dw291_t.webp"],
            titles: ["opp 2000", "fabia", "jetx", "dw 291"],
            text:   <div id="text_container">
                        <h1>DIGITAL</h1>
                        <h2>Digital production to make sense of reality</h2>
                        <p>Benefit from continuous research and hands-on experience from over 500 B2B projects in the digital universe. Let customers interact with your products in virtual environments and simulate scenarios for training and education. Present technological highlights with 3D animations and deploy uniform 3D visualisations across your entire range of products. Make use of our software and media assets across different platforms and a wide range of applications to guarantee consistent appearance of your products everywhere. Gain value from our broad and established knowledge in digital production, full stack software development and industrial processes.</p>
                        <br/>
                        <h2>Selected Projects</h2>
                    </div>,
            ids: ["opp2000", "fabia", "jetx", "dw291"],
            shorts: ["opp 2000", "fabia", "jetx", "dw 291"]
        },
        games: {
            srcs: ["/media/njbdesign_xthrust_t.webp", "/media/njbdesign_ammomaker_t.webp"],
            alts: ["njbdesign_xthrust_t.webp", "njbdesign_ammomaker_t.webp"],
            titles: ["xthrust", "ammo maker"],
            text:   <div id="text_container">
                        <h1>GAMES</h1>
                        <h2>Independent projects and unique assets that will keep you entertained</h2>
                        <p>Developing games is our source of innovation in the digital universe. As independent publisher we focus on a high degree of in-house development to accurately represent our ideas and concepts. Expect high-end assets produced according to our own quality standards and established workflows. Join over 300,000 players that appreciate our unique projects and direct engagement with the community already. Based on your feedback and our vision we specify new features and improve our projects continuously.</p>
                        <br/>
                        <h2>Selected Projects</h2>
                    </div>,
            ids: ["xthrust", "ammomaker"],
            shorts: ["xthrust", "ammo maker"]
        },
        design: {
            srcs: ["/media/njbdesign_curve_t.webp", "/media/njbdesign_wristband_t.webp"],
            alts: ["njbdesign_curve_t.webp", "njbdesign_wristband_t.webp"],
            titles: ["curve", "wristband"],
            text:   <div id="text_container">
                        <h1>DESIGN</h1>
                        <h2>Custom design and engineering for bespoke applications that reflects identity</h2>
                        <p>Follow our passion to design objects for real world applications. Benefit from our analysis of environments and functionality requirements to find timeless solutions in an ever-changing world. We abstract components to their essential functionality to gain space for the unconventional use of shapes and materials. Unleash the full potential of modern manufacturing technologies with our digital workflows and experience the direct paths from the virtual universe to reality. We coordinate the production of components with our partners and constantly optimize processes to allow the most efficient and accurate manufacturing.</p>
                        <br/>
                        <h2>Selected Projects</h2>
                    </div>,
            ids: ["curve", "wristband"],
            shorts: ["curve", "wristband"]
        },
        store: {
            srcs: ["/media/njbdesign_sticker_t.webp", "/media/njbdesign_ammomaker_birdfeeder_modelkit_t.webp", "/media/njbdesign_ammomaker_sticker_t.webp", "/media/njbdesign_xthrust_sticker_t.webp"],
            alts: ["njbdesign_sticker_t.webp", "njbdesign_ammomaker_birdfeeder_modelkit_t.webp", "njbdesign_ammomaker_sticker_t.webp", "njbdesign_xthrust_sticker_t.webp"],
            titles: ["njb design sticker", "bird feeder modelkit", "ammo maker sticker", "xthrust sticker"],
            text:   <div id="text_container">
                        <h1>STORE</h1>
                        <h2>Back our ventures by getting selected products to represent our brands</h2>
                        <p>Directly support our abilities to focus on research and projects for the digital community.</p>
                        <br/>
                        <h2>Selected Products</h2>
                    </div>,
            ids: ["njbdesign_sticker", "ammomaker_birdfeeder_modelkit", "ammomaker_sticker", "xthrust_sticker"],
            shorts: ["sticker", "model kit", "sticker", "sticker"]
        }
    };

    const pageKeyMap = {
        undefined: "home",
        about: "home"
    };

    const pageKey = pageKeyMap[page] ?? page;

    const contentData = contentMap[pageKey];

    const itemsLoader = <svg version="1.1" id="items_loader_svg" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 96 96" preserveAspectRatio="xMidYMid meet" pointerEvents="none">
                            <path id="items_loader_bg" fill="#000000ff" opacity="0.75" d="M68.167,81.938C66.242,85.272,61.517,88,57.667,88H15c-3.85,0-7-3.15-7-7V55c0-3.85,1.575-9.728,3.5-13.063l16.094-27.875C29.519,10.728,34.244,8,38.094,8H81c3.85,0,7,3.15,7,7v25.586c0,3.85-1.575,9.728-3.5,13.063L68.167,81.938z"/>
                            <linearGradient id="circle_gradient" gradientUnits="userSpaceOnUse" x1="34.5645" y1="61.4355" x2="61.4352" y2="34.5648">
                            	<stop offset="0" stopColor="#FFFFFF"/>
                            	<stop offset="0.25" stopColor="#FFFF00"/>
                            	<stop offset="0.5" stopColor="#CCFF33"/>
                            	<stop offset="0.75" stopColor="#00CCFF"/>
                            	<stop offset="1" stopColor="#FFFFFF"/>
                            </linearGradient>
                            <path id="items_loader_circle" fill="url(#circle_gradient)" d="M67,48c0,10.494-8.506,19-19,19s-19-8.506-19-19c0-10.495,8.506-19,19-19S67,37.505,67,48z M48,33c-8.283,0-15,6.716-15,15c0,8.283,6.717,15,15,15s15-6.717,15-15C63,39.716,56.283,33,48,33z"/>
                        </svg>;

    let pointerStartX = 0;
    let pointerCurrentX = 0;
    let scrollStartX = 0;
    let itemAction = false;
    let itemCursor = false;

    const itemWidth = 256;
    const itemGap = 32;

    function handleThumbnailClick(itemId) {

        let sectionName = GetSectionName(page);

        if (!page || page === "about") {

            if (itemId === section) {

                scrollToBottom("smooth");

            } else {

                navigate("/about/" + itemId);

            };

        } else if ("/" + page + "/" + sectionName + "/" + itemId === "/" + page + "/" + section + "/" + item) {

            scrollToBottom("smooth");

        } else if ("/" + page + "/" + sectionName + "/" + itemId === "/" + page + "/" + section + "/" + detailItemActiveRef.current) {

            navigate("/" + page + "/" + sectionName + "/" + itemId);

        } else {

            detailMediaLoadedRef.current = false;
            navigate("/" + page + "/" + sectionName + "/" + itemId);

        };

    };

    function getThumbnailPath(itemId) {

        let sectionName = GetSectionName(page);

        if (!page || page === "about") {

            return "/about/" + itemId;

        } else {

            return "/" + page + "/" + sectionName + "/" + itemId;

        };

    };

    function updateItemsScrollPositions() {

        scrollCenterRef.current = (itemsSectionRef.current.scrollWidth - window.innerWidth) / 2;
        scrollDistanceRef.current = (contentData.ids.length - 1) * (itemWidth + itemGap);
        scrollFirstPosRef.current = scrollCenterRef.current - scrollDistanceRef.current / 2;
        scrollLastPosRef.current = scrollCenterRef.current + scrollDistanceRef.current / 2;

    };

    function goToNextItem() {

        for (let i = 0; i <= contentData.ids.length - 1; i++) {

            if (scrollFirstPosRef.current + i * (itemGap + itemWidth) > itemsSectionRef.current.scrollLeft + 1) {
                scrollToItem(scrollFirstPosRef.current + i * (itemGap + itemWidth));
                break;
            };

        };

    };

    function goToPreviousItem() {

        for (let i = 0; i <= contentData.ids.length - 1; i++) {

            if (scrollLastPosRef.current - i * (itemGap + itemWidth) < itemsSectionRef.current.scrollLeft - 1) {
                scrollToItem(scrollLastPosRef.current - i * (itemGap + itemWidth));
                break;
            };

        };

    };

    function scrollToItem(position) {

        itemsSectionRef.current.scrollTo({
            top: 0,
            left: position,
            behavior: "smooth",
        });

    };

    function calcItemsContainerControlsVis() {
    
        if (contentData.ids.length > 1 && contentData.ids.length * itemWidth + (contentData.ids.length - 1) * itemGap > window.innerWidth) {
            if (itemsSectionRef.current.scrollLeft < scrollFirstPosRef.current + 64) {
                setItemsControlsLeftSvgStyleState(prevState => ({
                    ...prevState,
                    visibility: "hidden",
                }));
                setItemsControlsRightSvgStyleState(prevState => ({
                    ...prevState,
                    visibility: "visible",
                }));
            } else if (itemsSectionRef.current.scrollLeft > scrollLastPosRef.current - 64) {
                setItemsControlsLeftSvgStyleState(prevState => ({
                    ...prevState,
                    visibility: "visible",
                }));
                setItemsControlsRightSvgStyleState(prevState => ({
                    ...prevState,
                    visibility: "hidden",
                }));
            } else {
                setItemsControlsLeftSvgStyleState(prevState => ({
                    ...prevState,
                    visibility: "visible",
                }));
                setItemsControlsRightSvgStyleState(prevState => ({
                    ...prevState,
                    visibility: "visible",
                }));
            };
        } else {
            setItemsControlsLeftSvgStyleState(prevState => ({
                ...prevState,
                visibility: "hidden",
            }));
            setItemsControlsRightSvgStyleState(prevState => ({
                ...prevState,
                visibility: "hidden",
            }));
        };

    };

    function centerItemsSection() {

        itemsSectionRef.current.scrollLeft = scrollCenterRef.current;

    };

    function handleResize() {

        updateItemsScrollPositions();
        calcItemsContainerControlsVis();

    };

    function handleItemSectionScrollEnd() {

        calcItemsContainerControlsVis();

    };

    function handleItemSectionScroll() {

        calcItemsContainerControlsVis();

    };

    function mainContainerClick() {
        setDetailWidgetState(false);
        setWidgetState(false);
    }

    //pointer interaction
    function pointerDownItemSection(e) {
        if (e.pointerType === "mouse") {
            itemsSectionRef.current.onpointermove = pointerMoveItemSection;

            pointerStartX = e.screenX;
            scrollStartX = itemsSectionRef.current.scrollLeft;
        };
    };

    function pointerMoveItemSection(e) {
        if (e.pointerType === "mouse") {
            itemsSectionRef.current.setPointerCapture(e.pointerId);

            pointerCurrentX = e.screenX;

            let pointerCurrentXDist = pointerStartX > pointerCurrentX ? pointerStartX - pointerCurrentX : pointerCurrentX - pointerStartX;

            if (pointerCurrentXDist > 7) {
                itemAction = false;
                itemsSectionRef.current.style.cursor = 'grabbing';
            }
            
            itemsSectionRef.current.scrollLeft = scrollStartX + (pointerStartX - pointerCurrentX);
        };
    };

    function pointerUpItemSection(e, itemId) {
        if (e.pointerType === "mouse") {
            itemsSectionRef.current.onpointermove = null;
            itemsSectionRef.current.releasePointerCapture(e.pointerId);

            if (itemAction === true) {
                handleThumbnailClick(itemId);
                itemAction = false;
            };

            if (itemCursor === true) {
                itemsSectionRef.current.style.cursor = 'pointer';
            } else {
                itemsSectionRef.current.style.cursor = 'grab';
            };
            itemCursor = false;

            calcItemsContainerControlsVis();

        };
    };

    function pointerDownItem(e) {
        if (e.pointerType === "mouse" && e.button === 0) {
            itemAction = true;
            itemCursor = true;
        };
    };

    function pointerLeaveItem(e) {
        if (e.pointerType === "mouse") {
            itemsSectionRef.current.style.cursor = 'grab';
        };
    };

    //touch interaction
    function touchStartItem(e) {
        itemAction = true;
    };

    function touchMoveItemSection(e) {
        itemAction = false;
    };

    function touchEndItem(e, itemId) {
        if (itemAction === true) {

            handleThumbnailClick(itemId);
            itemAction = false;

        };

        calcItemsContainerControlsVis();

    };

    //useEffect on page parameter change
    useEffect(() => {

        updateItemsScrollPositions();
        calcItemsContainerControlsVis();
        centerItemsSection();

        let itemSectionRef = itemsSectionRef.current
        window.addEventListener("resize", handleResize);
        itemSectionRef.addEventListener("scroll", handleItemSectionScroll);
        itemSectionRef.addEventListener("scrollend", handleItemSectionScrollEnd);

        return () => {

            window.removeEventListener("resize", handleResize);
            itemSectionRef.removeEventListener("scroll", handleItemSectionScroll);
            itemSectionRef.removeEventListener("scrollend", handleItemSectionScrollEnd);

        };
                                                                                                                                                // eslint-disable-next-line
    }, [page]);

    return (
        <div id="main_container" style={mainContainerStyleState} onClick={mainContainerClick}>
            <div id="main_container_content" style={mainContainerContentStyleState} ref={pageElementsRefs.mainContainerContent}>
                {contentData.text}
                <div id="items_container">
                    <div id="items_section" ref={itemsSectionRef} style={{cursor: "grab"}} onPointerDown={pointerDownItemSection} onPointerUp={pointerUpItemSection} onTouchMove={touchMoveItemSection}>
                        <div id="items_container_content" style={itemsContainerContentStyleState}>
                            {
                                contentData.srcs.map((value, index)=>{
                                    return(
                                        <div id="thumbnail_container" key={index}>
                                            <img id="thumbnail_image" src={value} alt={contentData.alts[index]} width="100%" height="100%" />
                                            <p id="thumbnail_title">{contentData.shorts[index]}</p>
                                            <a href={getThumbnailPath(contentData.ids[index])} onPointerDown={(e) => {e.preventDefault()}} onPointerUp={(e) => {e.preventDefault()}} onClick={(e) => {e.preventDefault()}} aria-label={"go to " + contentData.titles[index]}>
                                                <svg version="1.1" id="thumbnail_link_svg" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 256 256" preserveAspectRatio="xMinYMin meet" pointerEvents="none">
                                                    <path 
                                                        id="thumbnail_link_area"
                                                        pointerEvents="auto"
                                                        onTouchStart={touchStartItem}
                                                        onTouchEnd={(e) => {touchEndItem(e, contentData.ids[index])}}
                                                        onPointerDown={pointerDownItem}
                                                        onPointerUp={(e) => {pointerUpItemSection(e, contentData.ids[index])}}
                                                        onPointerLeave={pointerLeaveItem}
                                                        opacity="0"
                                                        fill="#000000"
                                                        d="M85.9,0c-6.6,0-14.7,4.677-18,10.392L0,128v128h115.997c6.6,0,15.818-3.818,20.485-8.485L256,128V0H85.9z"
                                                    />
                                                </svg>
                                            </a>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    </div>
                    {detailMediaLoaderState === true ? itemsLoader : null}
                    <svg version="1.1" id="items_controls_left_svg" style={itemsControlsLeftSvgStyleState} xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" preserveAspectRatio="xMinYMid meet" pointerEvents="none">
                        <path id="detail_controls_left" fill="#FFFFFF" d="M16.825,22.003c-1.1,1.101-1.1,2.896,0,3.995l14.079,14.079c1.098,1.1,1.428,0.857,0.734-0.531l-6.514-13.02c-0.691-1.391-0.691-3.664,0-5.052l6.514-13.022c0.69-1.39,0.361-1.628-0.734-0.526L16.825,22.003z"/>
                        <rect id="detail_controls_left_button" opacity="0" x="0" y="0" width="48" height="48" pointerEvents="auto" onClick={() => {goToPreviousItem()}}/>
                    </svg>
                    <svg version="1.1" id="items_controls_right_svg" style={itemsControlsRightSvgStyleState} xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" preserveAspectRatio="xMaxYMid meet" pointerEvents="none">
                        <path id="detail_controls_right" fill="#FFFFFF" d="M31.175,25.997c1.1-1.1,1.1-2.896,0-3.995L17.096,7.923c-1.098-1.1-1.428-0.857-0.734,0.531l6.514,13.02c0.691,1.391,0.691,3.664,0,5.051l-6.514,13.022c-0.69,1.39-0.361,1.628,0.734,0.526L31.175,25.997z"/>
                        <rect id="detail_controls_right_button" opacity="0" x="0" y="0" width="48" height="48" pointerEvents="auto" onClick={() => {goToNextItem()}}/>
                    </svg>
                </div>
            </div>
        </div>
    );

};